/* Variant Showcase — Paolina Roma (Dawn)
   Custom element behind sections/pr-product-variant-showcase.liquid.

   A card click does NOT just preselect a finish: it adds that finish to the
   cart, exactly like the live Motion section. The flow is rewritten on Dawn's
   own APIs instead of Motion's timing guesses:

   1. find the main <product-info id="MainProduct-*"> (scoped so a quick-add
      or a recommendations picker can never be targeted by mistake)
   2. find the option input whose value matches data-variant-value
      (radio for the "button"/"swatch" picker, <option> for the dropdown ones)
   3. if it is already selected, submit right away: no `change` means Dawn
      never publishes variantChange and we would wait for nothing
   4. otherwise check it, dispatch `change`, and wait for the real
      variantChange event (Dawn only publishes it after the hidden id input,
      the price, the media and the submit button state are updated)
   5. re-query the form and its [name="add"] button — Dawn swaps those nodes
      through HTMLUpdateUtility.viewTransition, so nothing may be cached —
      then click it and let <product-form> run the native ajax add and open
      the cart drawer

   Sold out combinations get an inline message in the card instead of Motion's
   alert(). Requires constants.js + pubsub.js, already loaded in
   layout/theme.liquid. Self-initializing so it survives shopify:section:load
   in the theme editor; disconnectedCallback drops the subscription and the
   timers so a re-render can never leave two listeners adding twice. */

if (!customElements.get('pr-variant-showcase')) {
  customElements.define(
    'pr-variant-showcase',
    class PrVariantShowcase extends HTMLElement {
      connectedCallback() {
        this.addedLabel = this.dataset.addedLabel || 'Added';
        this.pending = false;
        this.unsubscribe = null;
        this.waitTimer = null;
        this.waitToken = 0;
        this.cooldownTimer = null;
        this.flashTimers = new Map();

        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.stopWaiting();
        clearTimeout(this.cooldownTimer);
        this.flashTimers.forEach((id) => clearTimeout(id));
        this.flashTimers.clear();
      }

      onClick(event) {
        const card = event.target.closest('.vs__card');
        if (!card || !this.contains(card)) return;

        event.preventDefault();
        if (this.pending) return;

        const value = (card.dataset.variantValue || '').trim();
        if (!value) return;

        this.setStatus(card, '');

        const info = document.querySelector('product-info[id^="MainProduct-"]');
        if (!info) return;

        const picker = info.querySelector('variant-selects');
        if (!picker) {
          this.scrollToPicker(info);
          return;
        }

        const needle = value.toLowerCase();
        const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
          (input) => (input.value || '').trim().toLowerCase() === needle
        );

        if (radio) {
          if (radio.checked) {
            this.submit(card);
          } else {
            this.startWaiting(card);
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        const select = Array.from(picker.querySelectorAll('select')).find((node) =>
          Array.from(node.options).some((option) => (option.value || '').trim().toLowerCase() === needle)
        );

        if (select) {
          const option = Array.from(select.options).find(
            (node) => (node.value || '').trim().toLowerCase() === needle
          );

          if (select.value === option.value) {
            this.submit(card);
          } else {
            this.startWaiting(card);
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        this.scrollToPicker(info);
      }

      /* Locks the section and waits for Dawn to finish its section fetch.

         The timeout only lifts the lock, it does NOT submit: Dawn disables
         [name="add"] for the whole fetch (product-info.js resetProductFormState
         -> product-form.js toggleSubmitButton(true)) and re-enables it only when
         the section HTML lands, one line before publishing variantChange. Asking
         submit() at 4s would therefore read a disabled button on an available
         product, print a false "Sold out" and drop the add for good. The
         subscription stays armed instead, so a slow fetch still ends in a real
         add to cart when it resolves.

         waitToken guards that surviving subscription: if the shopper clicks the
         other card meanwhile, the new startWaiting bumps the token and the stale
         callback returns without adding a second item. */
      startWaiting(card) {
        this.pending = true;
        card.setAttribute('aria-busy', 'true');
        this.stopWaiting();

        this.waitToken += 1;
        const token = this.waitToken;

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            if (token !== this.waitToken) return;
            this.stopWaiting();
            this.submit(card);
          });
        }

        this.waitTimer = setTimeout(() => {
          this.clearWaitTimer();
          this.release(card, 0);
        }, 4000);
      }

      clearWaitTimer() {
        clearTimeout(this.waitTimer);
        this.waitTimer = null;
      }

      stopWaiting() {
        this.clearWaitTimer();

        if (typeof this.unsubscribe === 'function') {
          this.unsubscribe();
          this.unsubscribe = null;
        }
      }

      submit(card) {
        const info = document.querySelector('product-info[id^="MainProduct-"]');
        const section = info && info.dataset.section;
        const form = section ? document.getElementById(`product-form-${section}`) : null;
        const button = form ? form.querySelector('[name="add"]') : null;
        const strings = window.variantStrings || {};

        if (!button) {
          this.release(card, 0);
          return;
        }

        if (button.hasAttribute('disabled')) {
          /* A wait still armed means Dawn is mid fetch and keeps the button
             disabled: not a real sold out, so no message. The subscription
             submits for real when the section HTML lands. */
          if (typeof this.unsubscribe === 'function') {
            this.release(card, 0);
            return;
          }

          /* Steady state: reuse the label Dawn just wrote in the button
             (soldOut, or "unavailable" after setUnavailable) instead of
             hardcoding sold out and duplicating Dawn's logic. */
          const buttonLabel = button.querySelector('span');
          const buttonText = buttonLabel ? buttonLabel.textContent.trim() : '';

          this.setStatus(card, buttonText || strings.soldOut || 'Sold out');
          this.release(card, 0);
          return;
        }

        button.click();
        this.flash(card);
        this.release(card, 800);
      }

      /* Short cooldown after handing the click over: it swallows a double
         click on the same card without freezing the other one for the whole
         length of the "Added" feedback. */
      release(card, delay) {
        card.removeAttribute('aria-busy');
        clearTimeout(this.cooldownTimer);

        if (!delay) {
          this.pending = false;
          return;
        }

        this.cooldownTimer = setTimeout(() => {
          this.pending = false;
        }, delay);
      }

      flash(card) {
        const label = card.querySelector('.vs__btn-txt');
        if (!label) return;

        if (!card.dataset.ctaLabel) {
          card.dataset.ctaLabel = label.textContent;
        }

        clearTimeout(this.flashTimers.get(card));
        label.textContent = this.addedLabel;
        card.classList.add('is-added');

        this.flashTimers.set(
          card,
          setTimeout(() => {
            label.textContent = card.dataset.ctaLabel;
            card.classList.remove('is-added');
            this.flashTimers.delete(card);
          }, 1800)
        );
      }

      /* Two destinations: the visible span inside the card button (aria-hidden,
         because role=button hides its descendants from the accessibility tree)
         and the single visually-hidden live region rendered outside the buttons,
         which is the only one screen readers announce. The finish name is
         prefixed there so it is clear which of the two cards is out of stock. */
      setStatus(card, message) {
        const status = card.querySelector('.vs__status');
        if (status) status.textContent = message;

        const live = this.querySelector('[data-vs-live]');
        if (!live) return;

        if (!message) {
          live.textContent = '';
          return;
        }

        const label = card.querySelector('.vs__label');
        const name = label ? label.textContent.trim() : '';
        const announcement = name ? `${name}: ${message}` : message;

        /* An unchanged value is not read again, so the region is emptied and
           rewritten on the next frame. */
        live.textContent = '';

        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => {
            live.textContent = announcement;
          });
        } else {
          live.textContent = announcement;
        }
      }

      scrollToPicker(info) {
        const target = info.querySelector('variant-selects') || info.querySelector('.product-form') || info;
        const reduce =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      }
    }
  );
}
